import React, { useState } from 'react';
import { SBA } from '../theme';
import { formatUsd } from '../data/studyPlans';

/**
 * Bank-hosted checkout (Meezan-style).
 * Card number, name, expiry, and CVC are never collected or stored here.
 * The customer pays on the bank's page; this app only keeps the order reference.
 */
export default function PaymentGateway({
  amount,
  title = 'Secure bank checkout',
  summaryLines = [],
  onPay,
  onCancel,
  onFail,
  payLabel,
}) {
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setProcessing(true);
    try {
      await onPay();
    } catch (err) {
      console.error(err);
      if (typeof onFail === 'function') {
        onFail(err);
        return;
      }
      setError(err?.message || 'Payment could not be started. Please try again.');
      setProcessing(false);
    }
  }

  return (
    <div style={g.wrap}>
      <div style={g.summaryCol}>
        <div style={g.brandRow}>
          <div style={g.brandMark}>
            <span className="material-symbols-outlined" style={{ fontSize: 20, color: SBA.gold }}>
              account_balance
            </span>
          </div>
          <div>
            <p style={g.brandName}>Success Bridge Academy</p>
            <p style={g.brandSub}>Hosted bank checkout</p>
          </div>
        </div>

        <p style={g.amountLabel}>Total due</p>
        <p style={g.amount}>{formatUsd(amount)}</p>

        <div style={g.summaryBox}>
          {summaryLines
            .filter((line) => line.label !== 'Student')
            .map(({ label, value }) => (
              <div key={label} style={g.summaryLine}>
                <span style={g.summaryKey}>{label}</span>
                <span style={g.summaryVal}>{value}</span>
              </div>
            ))}
        </div>

        <div style={g.secureRow}>
          <span className="material-symbols-outlined" style={{ fontSize: 16 }}>lock</span>
          Card details stay on the bank’s page
        </div>
      </div>

      <form onSubmit={handleSubmit} style={g.formCol}>
        <h2 style={g.formTitle}>{title}</h2>
        <p style={g.lead}>
          Payment is completed on a secure page hosted by the bank (such as Meezan Bank).
          This page does not ask for or show card or customer details.
        </p>

        <ul style={g.list}>
          <li>You confirm the order here, then pay on the bank’s checkout.</li>
          <li>Only an order reference, amount, and payment status are kept.</li>
        </ul>

        {error ? <div style={g.error}>{error}</div> : null}

        <button type="submit" disabled={processing} style={g.payBtn(processing)}>
          {processing ? (
            <>
              <span className="sba-spin" style={g.spinner} />
              Opening bank checkout…
            </>
          ) : (
            payLabel || `Continue to bank — ${formatUsd(amount)}`
          )}
        </button>

        {onCancel ? (
          <button type="button" onClick={onCancel} disabled={processing} style={g.cancelBtn}>
            Back
          </button>
        ) : null}

        <p style={g.footNote}>
          Third-party hosted checkout. No card or customer payment data is stored by this app.
        </p>
      </form>
    </div>
  );
}

const g = {
  wrap: {
    display: 'grid',
    gridTemplateColumns: 'minmax(240px, 380px) minmax(280px, 1fr)',
    borderRadius: 20,
    overflow: 'hidden',
    border: '1px solid #e2e8f0',
    boxShadow: '0 16px 44px rgba(27,54,93,0.14)',
    background: '#fff',
  },
  summaryCol: {
    background: `linear-gradient(160deg, ${SBA.navy} 0%, ${SBA.navyDeep} 70%)`,
    color: '#fff',
    padding: '28px 26px',
    display: 'flex',
    flexDirection: 'column',
  },
  brandRow: { display: 'flex', gap: 12, alignItems: 'center', marginBottom: 26 },
  brandMark: {
    width: 40,
    height: 40,
    borderRadius: 12,
    background: 'rgba(197,160,70,0.15)',
    border: '1px solid rgba(197,160,70,0.4)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  brandName: { fontWeight: 800, fontSize: 14 },
  brandSub: { fontSize: 11, opacity: 0.75 },
  amountLabel: {
    fontSize: 11,
    fontWeight: 800,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    color: SBA.gold,
    marginBottom: 4,
  },
  amount: { fontSize: 36, fontWeight: 800, marginBottom: 20 },
  summaryBox: {
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: 14,
    padding: '14px 16px',
    display: 'grid',
    gap: 10,
    marginBottom: 18,
  },
  summaryLine: { display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 13 },
  summaryKey: { opacity: 0.75 },
  summaryVal: { fontWeight: 700, textAlign: 'right' },
  secureRow: {
    marginTop: 'auto',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 12,
    opacity: 0.8,
  },
  formCol: { padding: '28px 26px', display: 'grid', gap: 14, alignContent: 'start' },
  formTitle: { margin: 0, fontSize: 18, fontWeight: 800, color: SBA.navy },
  lead: { margin: 0, fontSize: 14, lineHeight: 1.55, color: '#475569' },
  list: { margin: 0, paddingLeft: 18, fontSize: 13, lineHeight: 1.6, color: '#475569' },
  error: {
    padding: '10px 12px',
    borderRadius: 10,
    background: '#fef2f2',
    color: '#b91c1c',
    fontSize: 13,
    fontWeight: 600,
  },
  payBtn: (disabled) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    padding: '14px 18px',
    borderRadius: 12,
    border: 'none',
    background: disabled ? '#94a3b8' : `linear-gradient(135deg, ${SBA.gold} 0%, ${SBA.goldStrong} 100%)`,
    color: '#1a1405',
    fontWeight: 800,
    fontSize: 15,
    cursor: disabled ? 'not-allowed' : 'pointer',
  }),
  spinner: {
    width: 16,
    height: 16,
    borderRadius: '50%',
    border: '2px solid rgba(26,20,5,0.3)',
    borderTopColor: '#1a1405',
    display: 'inline-block',
  },
  cancelBtn: {
    padding: '10px 14px',
    borderRadius: 10,
    border: '1.5px solid #e2e8f0',
    background: '#fff',
    color: '#475569',
    fontWeight: 700,
    fontSize: 13,
    cursor: 'pointer',
  },
  footNote: { fontSize: 11, color: '#94a3b8', textAlign: 'center', margin: 0 },
};
